import React, { useEffect, useState, useContext, useRef } from 'react';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { 
    Search, User, Activity, Trash2, Edit, Eye, X, Droplet, AlertTriangle, MapPin, Phone, Calendar, Stethoscope
} from 'lucide-react';

const REGIONS_DATA = {
    "г. Душанбе": ["р. Исмоили Сомони", "р. Сино", "р. Фирдавси", "р. Шохмансур"],
    "Согдийская область": ["Худжанд", "Исфара", "Истаравшан", "Канибадам", "Пенджикент", "Гулистон", "Б. Гафуров"],
    "Хатлонская область": ["Бохтар (Курган-Тюбе)", "Куляб", "Нурек", "Дангара", "Яван", "Фархор", "Восе"],
    "Районы республиканского подчинения": ["Вахдат", "Гиссар", "Турсунзаде", "Рашт", "Рогон", "Рудаки"],
    "Горно-Бадахшанская автономная область": ["Хорог", "Дарваз", "Вандж", "Рушан", "Ишкашим", "Мургаб"]
};

const formatPhone = (value) => {
    if (!value) return '+992 ';
    const numbers = value.replace(/\D/g, '');
    const tjNum = numbers.startsWith('992') ? numbers.slice(3) : numbers;
    let res = '+992 ';
    if (tjNum.length > 0) res += tjNum.substring(0, 3);
    if (tjNum.length > 3) res += '-' + tjNum.substring(3, 6);
    if (tjNum.length > 6) res += '-' + tjNum.substring(6, 9);
    return res;
};

const formatAddress = (addressStr) => {
    if (!addressStr) return "Адрес не указан";
    if (addressStr.includes('|||')) return addressStr.split('|||').filter(Boolean).join(', ');
    return addressStr;
};

const formatPassport = (value) => {
    const numbers = value.replace('A ', '').replace(/\D/g, '');
    return 'A ' + numbers.substring(0, 8);
};

const formatINN = (value) => {
    return value.replace(/\D/g, '').substring(0, 9);
};

// --- КОМПОНЕНТ ДЛЯ МНОЖЕСТВЕННОГО ВЫБОРА С ПОИСКОМ ---
const CustomMultiSelect = ({ value, onChange, options, placeholder, tagColor, tagBg }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef(null);

    const selectedArray = value ? value.split(', ').filter(Boolean) : [];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleItem = (itemValue) => {
        let newArray;
        if (selectedArray.includes(itemValue)) {
            newArray = selectedArray.filter(i => i !== itemValue);
        } else {
            newArray = [...selectedArray, itemValue];
        }
        onChange(newArray.join(', '));
        setSearch(''); 
    };

    const removeItem = (e, itemValue) => {
        e.stopPropagation();
        const newArray = selectedArray.filter(i => i !== itemValue);
        onChange(newArray.join(', '));
    };

    const filteredOptions = options
        .filter(o => o.label.toLowerCase().includes(search.toLowerCase()) && !selectedArray.includes(o.value))
        .slice(0, 5);

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            <div 
                onClick={() => setIsOpen(true)} 
                style={{ 
                    minHeight: '42px', padding: '6px 10px', borderRadius: '8px', border: '1px solid #d1d5db', 
                    background: 'white', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', cursor: 'text' 
                }}
            >
                {selectedArray.map(item => (
                    <span key={item} style={{ 
                        background: tagBg || '#f3f4f6', color: tagColor || '#374151', border: `1px solid ${tagColor}40`, 
                        padding: '2px 8px', borderRadius: '6px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '4px' 
                    }}>
                        {item}
                        <X size={14} style={{ cursor: 'pointer' }} onClick={(e) => removeItem(e, item)} />
                    </span>
                ))}
                <input 
                    type="text" 
                    value={search}
                    onChange={e => { setSearch(e.target.value); setIsOpen(true); }}
                    placeholder={selectedArray.length === 0 ? placeholder : ''}
                    style={{ border: 'none', outline: 'none', flex: 1, minWidth: '80px', fontSize: '0.95rem', background: 'transparent', color: '#111827' }}
                />
            </div>
            {isOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', zIndex: 100 }}>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '4px' }}>
                        {filteredOptions.length > 0 ? filteredOptions.map(opt => (
                            <div 
                                key={opt.value} 
                                onClick={() => toggleItem(opt.value)} 
                                style={{ padding: '8px 12px', cursor: 'pointer', borderRadius: '6px', fontSize: '0.9rem', color: '#374151' }}
                                onMouseOver={e => e.target.style.background = '#f3f4f6'} 
                                onMouseOut={e => e.target.style.background = 'transparent'}
                            >
                                {opt.label}
                            </div>
                        )) : (
                            <div style={{ padding: '8px 12px', color: '#9ca3af', textAlign: 'center', fontSize: '0.9rem' }}>
                                {search ? 'Ничего не найдено' : 'Начните ввод...'}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};


const Patients = () => {
    const { user } = useContext(AuthContext);
    const [patients, setPatients] = useState([]);
    const [hospitals, setHospitals] = useState([]);
    const [filteredPatients, setFilteredPatients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    const [allergiesOptions, setAllergiesOptions] = useState([]);
    const [chronicOptions, setChronicOptions] = useState([]);

    const [visibleCount, setVisibleCount] = useState(10);

    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [currentPatient, setCurrentPatient] = useState(null);
    
    const [editForm, setEditForm] = useState({
        full_name: '', phone: '', birth_date: '', gender: '', 
        blood_type: '', allergies: '', chronic_diseases: '',
        region: '', city: '', street: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const chiefHospital = user?.role === 'chief_doctor' ? hospitals.find(h => h.chief_doctor_id === user.id) : null;
    
    const allowedPatients = user?.role === 'chief_doctor' 
        ? patients.filter(p => chiefHospital && p.doctor?.hospital_id === chiefHospital.id)
        : patients;

    useEffect(() => {
        const lowerQ = searchQuery.toLowerCase();
        const filtered = allowedPatients.filter(p => 
            (p.full_name || '').toLowerCase().includes(lowerQ) ||
            (p.phone || '').includes(lowerQ) ||
            (p.medical_number || '').toLowerCase().includes(lowerQ)
        );
        setFilteredPatients(filtered);
        setVisibleCount(10);
    }, [searchQuery, patients, hospitals, user]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 200) {
                setVisibleCount(prev => prev + 10);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [patRes, hospRes, allergiesRes, chronicRes] = await Promise.all([
                api.get('/patients'),
                api.get('/hospitals').catch(() => ({ data: [] })),
                api.get('/dict/allergies').catch(() => ({ data: [] })),
                api.get('/dict/chronic').catch(() => ({ data: [] }))
            ]);
            
            setPatients(patRes.data || []);
            setHospitals(hospRes.data || []);
            
            // --- ИСПРАВЛЕНИЕ: Вытаскиваем "name" из JSON-строки ---
            const extractName = (jsonString) => {
                try {
                    const parsed = JSON.parse(jsonString);
                    return parsed.name || jsonString; // Если есть поле name - берем его, иначе саму строку
                } catch (e) {
                    return jsonString; // Если это не JSON, возвращаем как есть
                }
            };

            setAllergiesOptions((allergiesRes.data || []).map(item => {
                const name = extractName(item.data);
                return { label: name, value: name };
            }));
            
            setChronicOptions((chronicRes.data || []).map(item => {
                const name = extractName(item.data);
                return { label: name, value: name };
            }));

        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const calculateAge = (dateString) => {
        if (!dateString) return "Не указан";
        const today = new Date();
        const birthDate = new Date(dateString);
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age + " лет";
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Удалить пациента?")) return;
        try {
            await api.delete(`/patients/${id}`);
            setPatients(patients.filter(p => p.id !== id));
        } catch (error) {
            alert("Ошибка удаления");
        }
    };

    const handleEditClick = (patient) => {
        setCurrentPatient(patient);

        let region = ''; let city = ''; let street = patient.address || '';
        if (patient.address && patient.address.includes('|||')) {
            const parts = patient.address.split('|||');
            region = parts[0] || ''; city = parts[1] || ''; street = parts[2] || '';
        }

        setEditForm({
            full_name: patient.full_name || '',
            phone: patient.phone || '+992 ',
            birth_date: patient.birth_date || '',
            gender: patient.gender || 'Мужской',
            blood_type: patient.blood_type || '',
            allergies: patient.allergies || '',
            chronic_diseases: patient.chronic_diseases || '',
            region: region,
            city: city,
            street: street,
            passport: patient.passport || '',
            inn: patient.inn || ''
        });
        setShowEditModal(true);
    };

    const handleEditSave = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...editForm };
            payload.phone = payload.phone.replace(/[^\d+]/g, '');
            payload.address = `${payload.region}|||${payload.city}|||${payload.street}`;
            
            delete payload.region;
            delete payload.city;
            delete payload.street;

            const res = await api.put(`/patients/${currentPatient.id}`, payload);
            const updated = patients.map(p => p.id === currentPatient.id ? res.data : p);
            setPatients(updated);
            setShowEditModal(false);
        } catch (error) {
            alert("Ошибка сохранения");
        }
    };

    const handleViewClick = (patient) => {
        setCurrentPatient(patient);
        setShowViewModal(true);
    };

    const visiblePatients = filteredPatients.slice(0, visibleCount);

    return (
        <div>
            <div className="page-header-row" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                <div>
                    <h2 className="page-title">Пациенты</h2>
                    <p className="page-subtitle">Найдено: {filteredPatients.length} из {allowedPatients.length}</p>
                </div>
            </div>

            <div className="search-card">
                <div className="search-inner-wrapper">
                    <Search className="search-icon-lg" size={24} color="#9ca3af" />
                    <input 
                        type="text" 
                        className="search-input-lg p-search-input" 
                        placeholder="Поиск по ФИО, медномеру или телефону..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            <div className="patients-list" id="patientsList">
                {isLoading ? (
                    <p style={{textAlign: 'center', padding: '20px', color: '#6b7280'}}>Загрузка...</p>
                ) : visiblePatients.length === 0 ? (
                    <p style={{textAlign: 'center', padding: '40px', color: '#9ca3af', width: '100%', gridColumn: '1 / -1'}}>Ничего не найдено</p>
                ) : (
                    visiblePatients.map(p => (
                        <div key={p.id} className="patient-card">
                            <div className="p-card-body">
                                <div className="p-main-info">
                                    <div className="p-profile-block">
                                        <div className="p-avatar-box" style={{ backgroundColor: p.gender === 'Женский' ? '#fce7f3' : '#dbeafe' }}>
                                            <User size={24} className={p.gender === 'Женский' ? 'text-pink-600' : 'text-blue-600'} />
                                        </div>
                                        <div className="p-text-info">
                                            <div className="p-header-row">
                                                <h3 className="p-name p-searchable-name">{p.full_name}</h3>
                                            </div>
                                            <p className="p-meta">
                                                Медномер: <span className="mono-font p-searchable-id">{p.medical_number || `M-${p.id}`}</span>
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div className="p-details-grid">
                                        <div className="p-detail-item">
                                            <p className="p-label">Возраст / Пол</p>
                                            <p className="p-value">{calculateAge(p.birth_date)} / {p.gender}</p>
                                        </div>
                                        <div className="p-detail-item">
                                            <p className="p-label">Телефон</p>
                                            <p className="p-value p-searchable-phone">{formatPhone(p.phone)}</p>
                                        </div>
                                        <div className="p-detail-item">
                                            <p className="p-label">Группа крови</p>
                                            <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                                <Droplet size={14} className="text-red" />
                                                <p className="p-value">{p.blood_type || 'Не указана'}</p>
                                            </div>
                                        </div>
                                        <div className="p-detail-item">
                                            <p className="p-label">Регистрация</p>
                                            <p className="p-value">{new Date(p.created_at).toLocaleDateString()}</p>
                                        </div>
                                    </div>

                                    <div className="p-alerts-group" style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
                                        {p.allergies && (
                                            <div className="p-alerts" style={{marginTop: '0', width: '50%'}}>
                                                <p className="alert-title text-red" style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                                                    <AlertTriangle size={14}/> Аллергии:
                                                </p>
                                                <p className="alert-value">{p.allergies}</p>
                                            </div>
                                        )}
                                        {p.chronic_diseases && (
                                            <div className="p-alerts" style={{marginTop:'0', width: '50%'}}>
                                                <p className="alert-title text-orange" style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                                                    <Activity size={14}/> Хронические заболевания:
                                                </p>
                                                <p className="alert-value">{p.chronic_diseases}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="p-card-actions">
                                <button className="btn-action btn-view" onClick={() => handleViewClick(p)}>
                                    <Eye size={16} /> Подробнее
                                </button>
                                <button className="btn-action btn-edit" onClick={() => handleEditClick(p)}>
                                    <Edit size={16} /> Изменить
                                </button>
                                {(user?.role === 'super_admin' || user?.role === 'admin') && (
                                    <button className="btn-action btn-delete" onClick={() => handleDelete(p.id)}>
                                        <Trash2 size={16} /> Удалить
                                    </button>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {!isLoading && visiblePatients.length < filteredPatients.length && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    Прокрутите вниз, чтобы загрузить еще...
                </div>
            )}

            {showEditModal && (
                <div style={modalOverlayStyle}>
                    <div style={{...modalStyle, width: '600px', padding: '32px'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px'}}>
                            <div>
                                <h3 style={{fontWeight: 'bold', fontSize: '1.25rem', color: '#111827', margin: 0}}>Редактирование профиля</h3>
                                <p style={{fontSize: '0.85rem', color: '#6b7280', margin: 0}}>Обновите личные и медицинские данные пациента</p>
                            </div>
                            <button type="button" onClick={() => setShowEditModal(false)} style={iconBtnStyle}><X size={20}/></button>
                        </div>

                        <form onSubmit={handleEditSave} style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                            
                            <div style={formSectionStyle}>
                                <h4 style={sectionTitleStyle}><User size={16}/> Основная информация</h4>
                                <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '16px'}}>
                                    <div>
                                        <label style={labelStyle}>ФИО Пациента *</label>
                                        <input className="form-input" style={inputStyle} value={editForm.full_name} onChange={e => setEditForm({...editForm, full_name: e.target.value})} required />
                                    </div>
                                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px'}}>
                                        <div>
                                            <label style={labelStyle}>Телефон *</label>
                                            <input className="form-input" style={inputStyle} value={formatPhone(editForm.phone)} onChange={e => setEditForm({...editForm, phone: formatPhone(e.target.value)})} required />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Дата рождения</label>
                                            <input type="date" className="form-input" style={inputStyle} value={editForm.birth_date} onChange={e => setEditForm({...editForm, birth_date: e.target.value})} />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Пол</label>
                                            <select className="form-input" style={inputStyle} value={editForm.gender} onChange={e => setEditForm({...editForm, gender: e.target.value})}>
                                                <option value="Мужской">Мужской</option>
                                                <option value="Женский">Женский</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px'}}>
                                        <div>
                                            <label style={labelStyle}>Паспорт</label>
                                            <input 
                                                className="form-input" 
                                                style={inputStyle} 
                                                value={editForm.passport.startsWith('A ') ? editForm.passport : 'A ' + editForm.passport.replace('A ', '')} 
                                                onChange={e => setEditForm({...editForm, passport: formatPassport(e.target.value)})} 
                                                placeholder="A XXXXXXXX" 
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>ИНН</label>
                                            <input 
                                                className="form-input" 
                                                style={inputStyle} 
                                                value={editForm.inn} 
                                                onChange={e => setEditForm({...editForm, inn: formatINN(e.target.value)})} 
                                                placeholder="XXXXXXXXX" 
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={formSectionStyle}>
                                <h4 style={sectionTitleStyle}><MapPin size={16}/> Место жительства</h4>
                                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px'}}>
                                    <div>
                                        <label style={labelStyle}>Область / Регион</label>
                                        <select className="form-input" style={inputStyle} value={editForm.region} onChange={e => setEditForm({...editForm, region: e.target.value, city: '', street: ''})}>
                                            <option value="">-- Выберите регион --</option>
                                            {Object.keys(REGIONS_DATA).map(r => <option key={r} value={r}>{r}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Город / район</label>
                                        <select className="form-input" style={inputStyle} value={editForm.city} onChange={e => setEditForm({...editForm, city: e.target.value, street: ''})} disabled={!editForm.region}>
                                            <option value="">-- Выберите город --</option>
                                            {editForm.region && REGIONS_DATA[editForm.region].map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label style={labelStyle}>Улица, дом, квартира</label>
                                    <input className="form-input" style={inputStyle} value={editForm.street} onChange={e => setEditForm({...editForm, street: e.target.value})} disabled={!editForm.city} placeholder="ул. Айни, дом 10, кв 5" />
                                </div>
                            </div>

                            <div style={{...formSectionStyle, background: '#fef2f2', borderColor: '#fee2e2'}}>
                                <h4 style={{...sectionTitleStyle, color: '#991b1b'}}><Activity size={16}/> Медицинские данные</h4>
                                <div style={{marginBottom: '16px'}}>
                                    <label style={labelStyle}>Группа крови</label>
                                    <select className="form-input" style={{...inputStyle, background: 'white'}} value={editForm.blood_type} onChange={e => setEditForm({...editForm, blood_type: e.target.value})}>
                                        <option value="">Не указана</option>
                                        <option value="O(I) Rh+">O(I) Rh+</option>
                                        <option value="O(I) Rh-">O(I) Rh-</option>
                                        <option value="A(II) Rh+">A(II) Rh+</option>
                                        <option value="A(II) Rh-">A(II) Rh-</option>
                                        <option value="B(III) Rh+">B(III) Rh+</option>
                                        <option value="B(III) Rh-">B(III) Rh-</option>
                                        <option value="AB(IV) Rh+">AB(IV) Rh+</option>
                                        <option value="AB(IV) Rh-">AB(IV) Rh-</option>
                                    </select>
                                </div>
                                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
                                    <div>
                                        <label style={{...labelStyle, color: '#991b1b'}}>Аллергии</label>
                                        <CustomMultiSelect 
                                            value={editForm.allergies} 
                                            onChange={val => setEditForm({...editForm, allergies: val})} 
                                            options={allergiesOptions} 
                                            placeholder="Поиск аллергии..." 
                                            tagColor="#991b1b"
                                            tagBg="#fef2f2"
                                        />
                                    </div>
                                    <div>
                                        <label style={{...labelStyle, color: '#9a3412'}}>Хронические заболевания</label>
                                        <CustomMultiSelect 
                                            value={editForm.chronic_diseases} 
                                            onChange={val => setEditForm({...editForm, chronic_diseases: val})} 
                                            options={chronicOptions} 
                                            placeholder="Поиск заболевания..." 
                                            tagColor="#9a3412"
                                            tagBg="#fff7ed"
                                        />
                                    </div>
                                </div>
                            </div>

                            <button type="submit" style={submitBtnStyle}>Сохранить изменения</button>
                        </form>
                    </div>
                </div>
            )}

            {showViewModal && currentPatient && (
                <div style={modalOverlayStyle}>
                    <div style={{...modalStyle, width: '550px', padding: 0, overflow: 'hidden'}}>
                        <div style={{background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', padding: '30px', position: 'relative'}}>
                            <button onClick={() => setShowViewModal(false)} style={{...iconBtnStyle, position: 'absolute', top: '15px', right: '15px', background: 'rgba(255,255,255,0.5)'}}><X size={20}/></button>
                            
                            <div style={{display: 'flex', gap: '20px', alignItems: 'center'}}>
                                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: currentPatient.gender === 'Женский' ? '#db2777' : '#2563eb', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                                    <User size={40} />
                                </div>
                                <div>
                                    <h3 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#1e3a8a', margin: '0 0 5px 0'}}>{currentPatient.full_name}</h3>
                                    <span style={{background: 'white', color: '#3b82f6', padding: '4px 10px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '600', display: 'inline-block'}}>
                                        {currentPatient.medical_number || `M-${currentPatient.id}`}
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <div style={{padding: '30px', overflowY: 'auto', height: '400px'}}>
                            
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px'}}>
                                <div style={infoBoxStyle}>
                                    <div style={infoBoxIconStyle}><Calendar size={18} color="#6366f1"/></div>
                                    <div>
                                        <p style={detailLabel}>Дата рождения</p>
                                        <p style={detailValue}>{currentPatient.birth_date} <span style={{color: '#6b7280', fontSize: '0.85rem'}}>({calculateAge(currentPatient.birth_date)})</span></p>
                                    </div>
                                </div>
                                <div style={infoBoxStyle}>
                                    <div style={infoBoxIconStyle}><Phone size={18} color="#10b981"/></div>
                                    <div>
                                        <p style={detailLabel}>Телефон</p>
                                        <p style={detailValue}>{formatPhone(currentPatient.phone)}</p>
                                    </div>
                                </div>
                                <div style={infoBoxStyle}>
                                    <div style={{...infoBoxIconStyle, color: currentPatient.gender === 'Женский' ? '#db2777' : '#3b82f6'}}><User size={18}/></div>
                                    <div>
                                        <p style={detailLabel}>Пол</p>
                                        <p style={detailValue}>{currentPatient.gender}</p>
                                    </div>
                                </div>
                                <div style={infoBoxStyle}>
                                    <div style={infoBoxIconStyle}><Droplet size={18} color="#ef4444"/></div>
                                    <div>
                                        <p style={detailLabel}>Группа крови</p>
                                        <p style={detailValue}>{currentPatient.blood_type || 'Не указана'}</p>
                                    </div>
                                </div>
                                <div style={infoBoxStyle}>
                                    <div style={infoBoxIconStyle}><User size={18} color="#6366f1"/></div>
                                    <div>
                                        <p style={detailLabel}>Паспорт</p>
                                        <p style={detailValue}>{currentPatient.passport || 'Не указан'}</p>
                                    </div>
                                </div>
                                <div style={infoBoxStyle}>
                                    <div style={infoBoxIconStyle}><User size={18} color="#10b981"/></div>
                                    <div>
                                        <p style={detailLabel}>ИНН</p>
                                        <p style={detailValue}>{currentPatient.inn || 'Не указан'}</p>
                                    </div>
                                </div>
                            </div>

                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px'}}>
                                <div style={infoBoxStyle}>
                                    <div style={infoBoxIconStyle}><MapPin size={18} color="#8b5cf6"/></div>
                                    <div>
                                        <p style={detailLabel}>Место жительства</p>
                                        <p style={detailValue}>{formatAddress(currentPatient.address)}</p>
                                    </div>
                                </div>
                                <div style={infoBoxStyle}>
                                    <div style={infoBoxIconStyle}><Stethoscope size={18} color="#f59e0b"/></div>
                                    <div>
                                        <p style={detailLabel}>Лечащий врач</p>
                                        <p style={detailValue}>
                                            {currentPatient.doctor ? (
                                                <>
                                                    {currentPatient.doctor.full_name} 
                                                    <span style={{display: 'block', color: '#6b7280', fontSize: '0.85rem', fontWeight: 'normal', marginTop: '2px'}}>
                                                        {currentPatient.doctor.specialization || 'Врач'}
                                                    </span>
                                                </>
                                            ) : (
                                                <span style={{color: '#9ca3af', fontWeight: 'normal'}}>Не назначен</span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {(currentPatient.allergies || currentPatient.chronic_diseases) && (
                                <div style={{background: '#fef2f2', padding: '20px', borderRadius: '12px', border: '1px dashed #fca5a5'}}>
                                    <h5 style={{fontWeight: 'bold', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: '#991b1b', margin: '0 0 12px 0'}}>
                                        <AlertTriangle size={18}/> Медицинские предупреждения
                                    </h5>
                                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                        {currentPatient.allergies && (
                                            <div style={{background: 'white', padding: '10px', borderRadius: '8px'}}>
                                                <span style={{fontWeight: '600', color: '#991b1b', display: 'block', fontSize: '0.85rem', marginBottom: '4px'}}>Аллергии:</span> 
                                                <span style={{color: '#111827', fontSize: '0.95rem'}}>{currentPatient.allergies}</span>
                                            </div>
                                        )}
                                        {currentPatient.chronic_diseases && (
                                            <div style={{background: 'white', padding: '10px', borderRadius: '8px'}}>
                                                <span style={{fontWeight: '600', color: '#9a3412', display: 'block', fontSize: '0.85rem', marginBottom: '4px'}}>Хронические заболевания:</span> 
                                                <span style={{color: '#111827', fontSize: '0.95rem'}}>{currentPatient.chronic_diseases}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' };
const modalStyle = { backgroundColor: 'white', borderRadius: '20px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', position: 'relative' };
const formSectionStyle = { padding: '20px', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#f8fafc' };
const sectionTitleStyle = { fontSize: '1rem', fontWeight: 'bold', color: '#374151', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#4b5563', marginBottom: '6px' };
const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', fontSize: '0.95rem', color: '#111827', background: 'white' };
const submitBtnStyle = { width: '100%', padding: '14px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '10px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s', marginTop: '10px' };
const iconBtnStyle = { background: '#f1f5f9', border: 'none', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', cursor: 'pointer', transition: 'all 0.2s' };
const infoBoxStyle = { display: 'flex', alignItems: 'flex-start', gap: '12px', background: '#f8fafc', padding: '16px', borderRadius: '12px' };
const infoBoxIconStyle = { background: 'white', padding: '8px', borderRadius: '10px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex' };
const detailLabel = { fontSize: '0.8rem', color: '#64748b', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', margin: 0 };
const detailValue = { fontSize: '1rem', fontWeight: '600', color: '#0f172a', margin: 0 };

export default Patients;